import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconInfoCircle } from '@tabler/icons-react';
import { featureHelp } from '@/lib/feature-glossary';

type Pos = { left: number; top: number; above: boolean };

/** Small "i" button that explains a feature in plain words. Opens on hover, focus or tap; Esc closes. */
export function InfoTip({ label, what, helps }: { label: string; what: string; helps: string }) {
  const id = useId();
  const btn = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<Pos | null>(null);
  const hoverTimer = useRef<number>();
  const openedAt = useRef(0);

  const place = useCallback(() => {
    const el = btn.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const w = Math.min(288, window.innerWidth - 24);
    const left = Math.max(12, Math.min(r.left + r.width / 2 - w / 2, window.innerWidth - w - 12));
    const above = r.bottom + 150 > window.innerHeight && r.top > 160;
    setPos({ left, top: above ? r.top - 8 : r.bottom + 8, above });
  }, []);

  const show = useCallback(() => { window.clearTimeout(hoverTimer.current); place(); setOpen((o) => { if (!o) openedAt.current = Date.now(); return true; }); }, [place]);
  const hide = useCallback(() => { window.clearTimeout(hoverTimer.current); hoverTimer.current = window.setTimeout(() => setOpen(false), 80); }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: PointerEvent) => { if (!btn.current?.contains(e.target as Node) && !(e.target as HTMLElement).closest?.('[data-infotip]')) setOpen(false); };
    const onMove = () => place();
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('scroll', onMove, { passive: true });
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('scroll', onMove);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  return (
    <>
      <button
        ref={btn}
        type="button"
        aria-label={`What is ${label}?`}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        className="infotip-btn"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (open && Date.now() - openedAt.current > 400) setOpen(false); else show(); }}
      >
        <IconInfoCircle size={15} stroke={1.5} aria-hidden="true" />
      </button>
      {open && pos && typeof document !== 'undefined' && createPortal(
        <div
          id={id}
          role="tooltip"
          data-infotip
          className="infotip-pop"
          style={{ left: pos.left, top: pos.top, transform: pos.above ? 'translateY(-100%)' : 'none' }}
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          <p className="infotip-title">{label}</p>
          <p className="infotip-what">{what}</p>
          <p className="infotip-helps"><span>How it helps</span>{helps}</p>
        </div>,
        document.body,
      )}
    </>
  );
}

/** A feature name followed by an info icon when the glossary knows it. Unknown labels render as plain text. */
export function FeatureLabel({ label, children }: { label: string; children?: React.ReactNode }) {
  const help = featureHelp(label);
  if (!help) return <>{children ?? label}</>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{children ?? label}</span>
      <InfoTip label={label.replace(/\s*\([^)]*\)\s*$/, '')} what={help.what} helps={help.helps} />
    </span>
  );
}
